import { TableCellTypes, Field, TextInput, InputStatuses } from '@carboledger/ui';
import {
  makeUnitTypeCell,
  makeUnitScopedCell,
  makeDiscrepanciesCell,
  makeRemoveCell,
  ReferenceIdField,
  batchFieldPrefix,
} from '@/components/common';
import { useTranslations, useRowUnitType } from '@/hooks';
import { rowUnitSymbol } from '@/lib/mappers/quantities';
import { buildRowUnitOptions } from './declarationUnits';

// An inbound row's columns: read-only in the picker, editable once it is allocated against.

const renderUnitTypeCell = makeUnitTypeCell((t, row) => buildRowUnitOptions(t, row.byUnit));
const renderBalanceQuantity = makeUnitScopedCell('balanceQuantity');
const renderAllocatedQuantity = makeUnitScopedCell('allocatedQuantity');

const textColumn = (id, header, width = 180) => ({ id, header, type: TableCellTypes.text, width });
const customColumn = (id, header, width, cell) => ({
  id,
  header,
  type: TableCellTypes.custom,
  width,
  cell,
});

// The same seven facts about a declaration, wherever the row is drawn.
const sustainabilityColumns = (t) => [
  textColumn('ghgEmissions', t('FIELD_GHG_EMISSIONS')),
  textColumn('ghgEmissionSavings', t('FIELD_GHG_EMISSION_SAVINGS')),
  textColumn('rawMaterialType', t('FIELD_RAW_MATERIAL_TYPE')),
  textColumn('countryOfOrigin', t('FIELD_COUNTRY_OF_ORIGIN')),
  textColumn('feedstockCategory', t('FIELD_FEEDSTOCK_CATEGORY')),
  textColumn('allocationPreference', t('FIELD_ALLOCATION_PREFERENCE')),
  { ...textColumn('dateOfDispatch', t('FIELD_DATE_OF_DISPATCH'), 150), type: TableCellTypes.date },
];

const unitTypeColumn = (t, unitHeaderSelect) => ({
  ...customColumn(
    'unitType',
    t('FIELD_UNIT_TYPE', { unit: unitHeaderSelect.value }),
    220,
    renderUnitTypeCell,
  ),
  headerSelect: unitHeaderSelect,
});

/**
 * @param unitHeaderSelect  the header's "switch every row" unit control
 * @param onOpenDiscrepancy (row) => void — opens the row's Discrepancies modal
 */
// The picker's own table — read-only; the amount is typed once the row is allocated against.
export const buildSelectInboundColumns = (t, { unitHeaderSelect, onOpenDiscrepancy }) => [
  textColumn('declarationNo', t('FIELD_DECLARATION_NO'), 160),
  unitTypeColumn(t, unitHeaderSelect),
  customColumn('balanceQuantity', t('FIELD_AVAILABLE_QUANTITY'), 180, renderBalanceQuantity),
  customColumn('allocatedQuantity', t('FIELD_ALLOCATED_QUANTITY'), 180, renderAllocatedQuantity),
  ...sustainabilityColumns(t),
  {
    ...textColumn('dateOfDischarge', t('FIELD_DATE_OF_DISCHARGE'), 150),
    type: TableCellTypes.date,
  },
  customColumn(
    'discrepancies',
    t('FIELD_DISCREPANCIES'),
    180,
    makeDiscrepanciesCell(onOpenDiscrepancy),
  ),
];

/**
 * Props:
 *   row  the picked row this cell belongs to
 */
// The same two boxes and Generate button every other form uses for an id.
const TransactionIdCell = ({ row }) => {
  const t = useTranslations();
  return (
    <ReferenceIdField
      namePrefix={batchFieldPrefix(row.id)}
      prefixLabel={t('ORDERS_FULFILL_TRANSACTION_ID_PREFIX')}
      numberLabel={t('ORDERS_FULFILL_TRANSACTION_ID_NUMBER')}
      required={false}
    />
  );
};

/**
 * Props:
 *   row  the picked row, which caps what can be certified against it
 */
const CertifiedQuantityCell = ({ row }) => {
  const t = useTranslations();
  const { unitType } = useRowUnitType(row.id, row.unitType);
  const unit = rowUnitSymbol(row, unitType);
  const cap = row.unassignedByUnit?.[unitType] ?? null;

  return (
    <Field name={`${batchFieldPrefix(row.id)}certifiedQuantity`}>
      {({ id, name, value, onChange, onBlur }) => {
        const amount = Number(value);
        const invalid = value !== '' && (!Number.isFinite(amount) || amount <= 0);
        const overCap = !invalid && value !== '' && cap != null && amount > cap;
        const message = overCap
          ? t('ORDERS_FULFILL_CERTIFIED_OVER_BALANCE')
          : invalid
            ? t('VALIDATION_POSITIVE_NUMBER')
            : null;
        return (
          <TextInput
            id={id}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={t('FORM_CERTIFIED_QUANTITY_PLACEHOLDER')}
            suffix={unit || undefined}
            status={message ? InputStatuses.error : undefined}
            message={message}
            aria-label={t('ALLOCATIONS_COL_CERTIFIED_QUANTITY')}
          />
        );
      }}
    </Field>
  );
};

const renderTransactionId = (ctx) => <TransactionIdCell row={ctx.row} />;
const renderCertifiedQuantity = (ctx) => <CertifiedQuantityCell row={ctx.row} />;

/**
 * @param unitHeaderSelect  the header's "switch every row" unit control
 * @param onRemove          (rowId) => void
 */
// The picked rows — the same declaration, now with the two amounts the fulfilment types in.
export const buildAllocateCreditColumns = (t, { unitHeaderSelect, onRemove }) => [
  customColumn('transactionId', t('ALLOCATIONS_COL_TRANSACTION_ID'), 240, renderTransactionId),
  textColumn('declarationNo', t('FIELD_DECLARATION_NO'), 160),
  unitTypeColumn(t, unitHeaderSelect),
  customColumn(
    'certifiedQuantity',
    t('ALLOCATIONS_COL_CERTIFIED_QUANTITY'),
    220,
    renderCertifiedQuantity,
  ),
  customColumn(
    'balanceQuantity',
    t('ALLOCATIONS_COL_BALANCE_QUANTITY'),
    180,
    renderBalanceQuantity,
  ),
  ...sustainabilityColumns(t),
  {
    ...customColumn('__remove__', t('FIELD_ACTION'), 120, makeRemoveCell(onRemove)),
    resizable: false,
  },
];
